'use client';

import { useQuery } from '@tanstack/react-query';

import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { fetchBestsellers } from '@eliadmin/core/src/components/nt/home/bestsellers-source';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma 168:14175: one row of four `Product` cards. */
const COUNT = 4;

const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1024px) calc((100vw - 136px) / 4), (min-width: 768px) calc((100vw - 96px) / 3), 260px';

/**
 * Bestseller tiles of the empty cart. The server renders the public list; a signed-in customer gets
 * the same request with the token (customer prices, favourites) – the homepage bestsellers query.
 * Four columns from lg, three from md, a scrolling row below.
 */
export function EmptyBestsellers({
  locale,
  products,
}: {
  locale: string;
  products: ProductDisplayData[];
}) {
  const authToken = useCustomerStore(state => state.me?.authToken);
  const { data: customerProducts } = useQuery({
    queryKey: ['nt-bestsellers', locale, authToken],
    queryFn: () => fetchBestsellers({ locale, authToken }),
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000,
  });
  const items = (authToken && customerProducts?.length ? customerProducts : products).slice(
    0,
    COUNT,
  );

  return (
    <ul className="hide-scroll-bar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 sm:-mx-6 sm:scroll-px-6 sm:px-6 md:mx-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:p-0 lg:grid-cols-4">
      {items.map((product, index) => (
        <li
          key={product.id}
          // Three columns at md: the fourth tile would sit alone on a second row.
          className={cn(
            'flex w-[260px] shrink-0 snap-start md:w-auto',
            index > 2 && 'md:max-lg:hidden',
          )}
        >
          <ProductTile product={product} imageSizes={TILE_SIZES} />
        </li>
      ))}
    </ul>
  );
}
